import CLIENT from "../../lib/api/client";
import ENDPOINTS from "../../lib/api/endpoints";

import { quantiseCoordinates, radiusToMeters } from "../search/supplierFilters";

export const RATING_TARGET_TYPE = "SAPPHIRE_SUPPLIER";

export const SORTS = Object.freeze({
  DISTANCE: "DISTANCE",
  PRICE_ASC: "PRICE_ASC",
  PRICE_DESC: "PRICE_DESC",
  DISCOUNTED: "DISCOUNTED",
  RATING: "RATING",
});

export const SUPPLIER_PRODUCTS_SIZE = 50;
export const NEARBY_OFFERS_SIZE = 200;

export const fetchProduct = async (productId) => {
  const { data } = await CLIENT.get(ENDPOINTS.CATALOG.productById(productId));

  return data;
};

export const fetchProductPackings = async (productId) => {
  const { data } = await CLIENT.get(ENDPOINTS.CATALOG.productPackings(productId));

  return data;
};

export const fetchProductSuppliers = async ({
  productId,
  coordinates,
  sort,
  maxDistanceKm,
  includeOutOfStock = false,
  discountedOnly = false,
}) => {
  const cell = quantiseCoordinates(coordinates);

  const params = {};

  if (cell) {
    params.latitude = cell.latitude;
    params.longitude = cell.longitude;

    if (Number.isFinite(maxDistanceKm) && maxDistanceKm > 0) {
      params.maxDistanceKm = Math.ceil(maxDistanceKm);
    }
  }

  const effectiveSort =
    sort === SORTS.DISTANCE && !cell ? SORTS.PRICE_ASC : sort;

  if (effectiveSort) params.sort = effectiveSort;
  if (includeOutOfStock) params.inStock = false;
  if (discountedOnly) params.discountedOnly = true;

  const { data } = await CLIENT.get(ENDPOINTS.CATALOG.productSuppliers(productId), {
    params,
  });

  return data?.suppliers || data?.offers || [];
};

export const fetchNearbyOffers = async ({
  coordinates,
  radiusKm,
  volume,
  containerOptionId,
}) => {
  const cell = quantiseCoordinates(coordinates);
  if (!cell) return [];

  const params = {
    "location.latitude": cell.latitude,
    "location.longitude": cell.longitude,
    "location.distanceMeters": radiusToMeters(radiusKm),
    size: NEARBY_OFFERS_SIZE,
  };

  if (Number.isFinite(volume)) {
    params.minVolume = volume;
    params.maxVolume = volume;
  }

  if (containerOptionId) params.containerOptionId = containerOptionId;

  const { data } = await CLIENT.get(ENDPOINTS.CATALOG.productsSearch, { params });

  return data?.content || [];
};

export const fetchSupplierDetail = async (supplierId) => {
  const { data } = await CLIENT.get(ENDPOINTS.SUPPLIERS.byId(supplierId));

  return data;
};

export const fetchSupplierProducts = async ({ supplierId, containerOptionId }) => {
  const params = { supplierId, size: SUPPLIER_PRODUCTS_SIZE };

  if (containerOptionId) params.containerOptionId = containerOptionId;

  const { data } = await CLIENT.get(ENDPOINTS.CATALOG.productsSearch, { params });

  return data?.content || [];
};

export const fetchSupplierAccessories = async (supplierId) => {
  const { data } = await CLIENT.get(ENDPOINTS.SUPPLIERS.accessories(supplierId));

  return data;
};

export const fetchCustomRefillQuote = async ({ supplierId, refillVolume, amount }) => {
  const body = amount ? { amount } : { refillVolume };

  const { data } = await CLIENT.post(
    ENDPOINTS.SUPPLIERS.customRefillQuote(supplierId),
    body
  );

  return data;
};

export const fetchContainerOptions = async () => {
  const { data } = await CLIENT.get(ENDPOINTS.TAXONOMY.containerOptions);

  return data?.containerOptions || [];
};

export const fetchSupplierRating = async (supplierId) => {
  const { data } = await CLIENT.get(ENDPOINTS.RATINGS.average, {
    params: { ratedTargetId: supplierId, ratedTargetType: RATING_TARGET_TYPE },
  });

  return {
    average: Number(data?.averageRating) || 0,
    total: Number(data?.totalRatings) || 0,
  };
};

export const fetchSupplierReviews = async ({ supplierId, page = 1, pageSize = 10 }) => {
  const { data } = await CLIENT.get(ENDPOINTS.RATINGS.listing, {
    params: {
      ratedTargetId: supplierId,
      ratedTargetType: RATING_TARGET_TYPE,
      page,
      pageSize,
    },
  });

  return {
    rows: data?.data || [],
    total: Number(data?.totalElements) || 0,
    totalPages: Number(data?.totalPages) || 0,
  };
};
